import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, LoaderIcon } from 'lucide-react';
const WEB3FORMS_KEY = '6ec2cd27-8115-4c87-a6ab-d5ef9765d214';
export function QuoteForm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.append('access_key', WEB3FORMS_KEY);
    formData.append('subject', 'New Quote Request — Strata Custom Apparel');
    formData.append('from_name', 'Strata Custom Apparel Website');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        form.reset();
        // Redirect to the dedicated confirmation page so the Meta Pixel can
        // register a PageView on /thank_you and count the conversion.
        navigate('/thank_you');
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  }
  return <section id="quote-form" className="scroll-mt-24 py-24 bg-gray-50 border-t-4 border-black" data-id="element-121">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-122">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white border-4 border-black p-8 md:p-12 shadow-2xl" data-id="element-123">
          <div className="text-center mb-10" data-id="element-124">
            <h2 className="text-4xl md:text-5xl font-black mb-4 uppercase" data-id="element-125">
              Request a Quote
            </h2>
            <p className="text-xl text-gray-600 font-medium" data-id="element-126">
              Tell us what you need and we’ll reply fast with pricing options.
            </p>
          </div>

          <motion.form key="form" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="space-y-6" onSubmit={handleSubmit} data-id="element-127">
            <input type="hidden" name="to" value="quotes@stratacustomapparel.com" data-id="element-128" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-129">
              <div data-id="element-130">
                <label htmlFor="name" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-131">
                  Name *
                </label>
                <input type="text" id="name" name="Name" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="John Doe" data-id="element-132" />
              </div>
              <div data-id="element-133">
                <label htmlFor="company" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-134">
                  Company Name
                </label>
                <input type="text" id="company" name="Company" disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Acme Corp" data-id="element-135" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-136">
              <div data-id="element-137">
                <label htmlFor="phone" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-138">
                  Phone *
                </label>
                <input type="tel" id="phone" name="Phone" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="(555) 123-4567" data-id="element-139" />
              </div>
              <div data-id="element-140">
                <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-141">
                  Email *
                </label>
                <input type="email" id="email" name="Email" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="john@example.com" data-id="element-142" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-143">
              <div className="md:col-span-1" data-id="element-144">
                <label htmlFor="product" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-145">
                  Product Needed *
                </label>
                <select id="product" name="Product Needed" required disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors bg-white disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-146">
                  <option value="" data-id="element-147">Select a product...</option>
                  <option value="T-Shirts" data-id="element-148">T-Shirts</option>
                  <option value="Polos" data-id="element-149">Polos</option>
                  <option value="Hats" data-id="element-150">Hats</option>
                  <option value="Uniforms" data-id="element-151">Uniforms</option>
                  <option value="Other" data-id="element-152">Other</option>
                </select>
              </div>
              <div className="md:col-span-1" data-id="element-153">
                <label htmlFor="quantity" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-154">
                  Quantity Needed *
                </label>
                <input type="number" id="quantity" name="Quantity Needed" required min="24" disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Min. 24" data-id="element-155" />
              </div>
              <div className="md:col-span-1" data-id="element-156">
                <label htmlFor="date" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-157">
                  Need By Date
                </label>
                <input type="date" id="date" name="Need By Date" disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-158" />
              </div>
            </div>

            <div data-id="element-159">
              <label htmlFor="notes" className="block text-sm font-bold uppercase tracking-wider mb-2" data-id="element-160">
                Project Details
              </label>
              <textarea id="notes" name="Project Details" rows={4} disabled={status === 'loading'} className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-colors resize-none placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Need different styles or quantities? Tell us a bit about your project." data-id="element-161"></textarea>
            </div>

            <AnimatePresence data-id="element-162">
              {status === 'error' && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="flex items-center gap-3 bg-red-50 border-2 border-red-200 p-4" data-id="element-163">
                  <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-164" />
                  <p className="text-red-700 font-medium" data-id="element-165">{errorMessage}</p>
                </motion.div>}
            </AnimatePresence>

            <motion.button whileHover={status !== 'loading' ? {
            scale: 1.02
          } : {}} whileTap={status !== 'loading' ? {
            scale: 0.98
          } : {}} type="submit" disabled={status === 'loading'} className="w-full bg-black text-white py-5 font-display font-black uppercase tracking-widest text-xl hover:bg-neutral-800 transition-colors mt-4 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3" data-id="element-166">
              {status === 'loading' ? <>
                  <LoaderIcon className="w-6 h-6 animate-spin" data-id="element-167" />
                  Sending...
                </> : 'Get My Fast Quote'}
            </motion.button>

            <p className="text-center text-sm text-gray-400 mt-3 font-medium" data-id="element-168">
              🔒 No spam, no commitments — just a fast, honest quote.
            </p>
          </motion.form>
        </motion.div>
      </div>
    </section>;
}